// src/components/SearchOverlay.js
import React, { useContext, useState, useEffect } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { getPublicCategories } from "../services/categories.api";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import { getHomepagePopularSearchOrder } from "../services/homepage.api";

// limit category titles to 11 characters
const truncateLabel = (text, max = 6) => {
  if (!text) return "";
  return text.length > max ? text.slice(0, max) + "..." : text;
};

function SearchOverlay({ isOpen, onClose = () => {} }) {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [categoryItems, setCategoryItems] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [popularOrder, setPopularOrder] = useState([]);

  /* ---------------- LOAD CATEGORIES + ORDER ---------------- */
  useEffect(() => {
    async function load() {
      try {
        const [cats, order] = await Promise.all([
          getPublicCategories(),
          getHomepagePopularSearchOrder(),
        ]);

        setPopularOrder(Array.isArray(order) ? order : []);

        const enriched = await Promise.all(
          cats.map(async (c) => {
            const products = await getAllProducts({ category: c.slug });
            const mapped = products
              .map(mapBackendProduct)
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            return {
              key: c.slug,
              labelEN: c.name_en,
              labelAR: c.name_ar,
              img: mapped[0]?.images?.[0] || "/images/placeholder-product.jpg",
            };
          })
        );

        setCategoryItems(enriched);
      } catch {}
    }

    if (isOpen) load();
  }, [isOpen]);

  /* ---------------- LIVE SUGGESTIONS ---------------- */
useEffect(() => {
  if (!query.trim() || query.trim().length < 2) {
    setSuggestions([]);
    return;
  }

  const t = setTimeout(async () => {
    try {
      setLoadingSuggest(true);
      const products = await getAllProducts({ search: query.trim() });
      const mapped = products.map(mapBackendProduct);

      const set = new Set();
      const q = query.trim().toLowerCase();

      mapped.forEach((p) => {
        if (p.nameEN?.toLowerCase().includes(q)) set.add(p.nameEN);
        if (p.nameAR?.toLowerCase().includes(q)) set.add(p.nameAR);

        if (p.public_id?.toLowerCase().includes(q)) set.add(p.public_id);

        p.categories?.forEach((c) => {
          if (c.toLowerCase().includes(q)) set.add(c);
        });

        p.colors?.forEach((c) => {
          if (c.name?.toLowerCase().includes(q)) set.add(c.name);
        });

        p.sizes?.forEach((s) => {
          if (String(s.size).toLowerCase().includes(q))
            set.add(String(s.size));
        });
      });

      setSuggestions(Array.from(set).slice(0, 3));
    } catch {
    } finally {
      setLoadingSuggest(false);
    }
  }, 250);

  return () => clearTimeout(t);
}, [query]);

  /* ---------------- POPULAR SEARCH ---------------- */
  const POPULAR_RAW = [
    ...categoryItems,
    { key: "new-in", labelEN: "New In", labelAR: "وصل حديثاً", customNew: true },
    { key: "sale", labelEN: "Sale", labelAR: "تخفيضات", customIcon: true },
    { key: "bestsellers", labelEN: "Bestsellers", labelAR: "الأكثر مبيعاً", customBest: true },
  ];

  const POPULAR = (() => {
    if (!popularOrder.length) return POPULAR_RAW;
    const idx = new Map(popularOrder.map((k, i) => [k, i]));
    return POPULAR_RAW.slice().sort(
      (a, b) => (idx.get(a.key) ?? 999) - (idx.get(b.key) ?? 999)
    );
  })();

  const submitSearch = () => {
    if (!query.trim()) return;
    onClose();
    navigate(`/products/search?q=${encodeURIComponent(query.trim())}`);
  };

  const goToCategory = (key) => {
  onClose();

  if (["sale", "new-in", "bestsellers"].includes(key)) {
    navigate(`/products/${key}`);
  } else {
    navigate(`/products/category/${key}`);
  }
};


  return (
    <div
      className={`fixed inset-0 z-[9999] transition-opacity duration-300 ${
        isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
    >
      {/* BACKDROP */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      {/* PANEL */}
 <div
  className={`absolute inset-0 bg-black px-6 pt-8 transform transition-transform duration-300 overflow-y-auto ${
    isOpen ? "translate-y-0" : "-translate-y-full"
  }`}
  dir={lang === "AR" ? "rtl" : "ltr"}
>
        {/* CLOSE */}
        <button
          onClick={onClose}
          className={`absolute top-4 text-3xl text-white opacity-80 hover:opacity-100 ${
            lang === "EN" ? "right-4" : "left-4"
          }`}
        >
          ×
        </button>

        <h2 className="text-xl font-bold text-white mb-5 tracking-wide">
          {lang === "EN" ? "Search" : "بحث"}
        </h2>

        {/* SEARCH BAR */}
        <div className="relative mb-8">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitSearch()}
            placeholder={lang === "EN" ? "Search products..." : "ابحث عن المنتجات..."}
            className="
              w-full rounded-full bg-[#0F1A12]
              border border-[#2E5A2A]
              py-3 pl-5 pr-12 text-sm
              text-white placeholder-[#8FAF8C]
              focus:outline-none
            "
          />

          {suggestions.length > 0 && (
            <div className="absolute mt-2 w-full bg-[#0F1A12] border border-[#2E5A2A] rounded-lg overflow-hidden">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => {
                    onClose();
                    navigate(`/products/search?q=${encodeURIComponent(s)}`);
                  }}
                  className="w-full px-4 py-3 text-left text-sm text-white hover:bg-[#1E2E20]"
                >
                  🔍 {s}
                </button>
              ))}
            </div>
          )}

          <button
            onClick={submitSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#2E5A2A] flex items-center justify-center"
          >
            <img src="/icons/search-white.png" className="w-4 h-4" />
          </button>
        </div>

        {/* POPULAR */}
        <p className="text-sm font-semibold text-white mb-4 tracking-wide">
          {lang === "EN" ? "Popular Searches" : "عمليات البحث الشائعة"}
        </p>

        <div className="flex flex-wrap gap-6">
          {POPULAR.map((item, i) => (
            <button
              key={i}
              onClick={() => goToCategory(item.key)}
              className="flex flex-col items-center"
            >
              <div className="w-16 h-16 rounded-full border-2 border-[#2E5A2A] overflow-hidden bg-black flex items-center justify-center">
                {!item.customIcon && !item.customBest && !item.customNew && (
                  <img src={item.img} className="w-full h-full object-cover" />
                )}

                {item.customIcon && (
                  <span className="text-xs font-bold text-red-500">SALE</span>
                )}
                {item.customBest && (
                  <span className="text-xs font-bold text-yellow-400">★ BEST</span>
                )}
                {item.customNew && (
                  <span className="text-xs font-bold text-white">NEW</span>
                )}
              </div>

              <span className="mt-2 text-xs px-4 py-1 rounded-full bg-[#2E5A2A] text-white">
                {truncateLabel(lang === "EN" ? item.labelEN : item.labelAR)}
              </span>
            </button>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 flex justify-center">
          <button
            onClick={() => {
              onClose();
              navigate("/all-products");
            }}
            className="
              w-full max-w-md py-3
              bg-[#2E5A2A] text-white
              rounded-full text-sm font-semibold
            "
          >
            {lang === "EN" ? "EXPLORE ALL PRODUCTS" : "تصفح كل المنتجات"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SearchOverlay;
